import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useAuth from "../../hooks/useAuth";
import { fetchCandidates } from "../../redux/slices/propertyCandidates";

const PropertyCandidatesDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [open, setOpen] = React.useState(false);
  const fetchedRef = useRef(false);
  const shownRef = useRef(false);

  const { generationStatus, candidatesReviewed, total, status } =
    useAppSelector((state) => state.propertyCandidates);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Fetch candidate status once per mount
  useEffect(() => {
    if (!associationId || fetchedRef.current) return;
    fetchedRef.current = true;
    dispatch(fetchCandidates(associationId));
  }, [associationId, dispatch]);

  // Show dialog once per mount when data is ready and review not yet done
  useEffect(() => {
    if (shownRef.current) return;
    if (status !== "idle") return;
    if (candidatesReviewed) return;
    if (generationStatus === "NOT_STARTED") return;
    shownRef.current = true;
    setOpen(true);
  }, [generationStatus, candidatesReviewed, status]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleGoToMap = () => {
    setOpen(false);
    navigate("/map?mode=candidates");
  };

  const renderContent = () => {
    if (generationStatus === "QUEUED" || generationStatus === "PROCESSING") {
      return (
        <>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <CircularProgress size={20} />
            <Typography>
              Etsimme kiinteistöjä tiesi varrelta. Tarkista ehdotukset hetken
              kuluttua kirjautumalla uudelleen sisään.
            </Typography>
          </Box>
        </>
      );
    }

    if (generationStatus === "FAILED") {
      return (
        <Typography>
          Kiinteistöjen automaattinen haku epäonnistui. Ota yhteyttä tukeen tai
          lisää kiinteistöt manuaalisesti.
        </Typography>
      );
    }

    if (generationStatus === "DONE" && total === 0) {
      return (
        <Typography>
          Emme löytäneet kiinteistöjä automaattisesti tien varrelta. Voit lisätä
          kiinteistöt manuaalisesti tiekunnanhallinnasta.
        </Typography>
      );
    }

    // DONE with candidates
    return (
      <Typography>
        Löysimme <strong>{total} kiinteistöehdokasta</strong> tiesi varrelta.
        Tarkista ehdotukset kartalla ja hyväksy tai hylkää kukin kiinteistö.
      </Typography>
    );
  };

  const showGoToMapButton = generationStatus === "DONE" && total > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kiinteistöehdotukset</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {showGoToMapButton ? "Myöhemmin" : "Sulje"}
        </Button>
        {showGoToMapButton && (
          <Button onClick={handleGoToMap} variant="contained">
            Tarkista kartalla
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PropertyCandidatesDialog;
